import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import {
  FaFilterCircleXmark,
  FaArrowDownShortWide,
  FaArrowUpShortWide,
} from "react-icons/fa6";

import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";

import StatusBadge from "../../components/activities/statusBadge";
import TypeBadge from "../../components/activities/typeBadge";
import RankingBadge from "../../components/activities/rankingBadge";

import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../config/apiConfig";

import {
  resetCoachActivities,
  storeCoachActivitiesPage,
} from "../../features/coachActivities/coachActivitiesSlice";

export default function CoachActivitiesPage() {
  const token = sessionStorage.getItem("access_token");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((s) => s.user.user);

  const cache = useSelector((s) => s.coachActivities.cache);
  const totalMap = useSelector((s) => s.coachActivities.total);

  const [activeTab, setActiveTab] = useState("mine"); // "mine" | "public"

  const [page, setPage] = useState(1);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [rankingTypeFilter, setRankingTypeFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("latest");

  // Search
  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const filterKey = `${activeTab}|${statusFilter}|${typeFilter}|${rankingTypeFilter}|${sortOrder}`;

  // ===================================================================
  // LOAD PAGE
  // ===================================================================
  useEffect(() => {
    if (!searching) {
      setPage(1);
      dispatch(resetCoachActivities());
      loadPage(1);
    }
  }, [activeTab, statusFilter, typeFilter, rankingTypeFilter, sortOrder]);

  async function loadPage(pageNumber) {
    if (cache[filterKey] && cache[filterKey][pageNumber]) {
      setActivities(cache[filterKey][pageNumber]);
      return;
    }

    setLoading(true);

    try {
      const url = new URL(
        activeTab === "mine"
          ? `${API_BASE}/api/coach-activities-list`
          : `${API_BASE}/api/activities-list`
      );

      url.searchParams.append("page", pageNumber);
      url.searchParams.append("limit", 8);
      url.searchParams.append("status", statusFilter);
      url.searchParams.append("type", typeFilter);
      url.searchParams.append("rankingType", rankingTypeFilter);
      url.searchParams.append("sort", sortOrder);

      if (activeTab === "public") {
        url.searchParams.append("visibility", "public");
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.success) {
        setActivities(data.activities);

        dispatch(
          storeCoachActivitiesPage({
            key: filterKey,
            page: pageNumber,
            items: data.activities,
            total: data.total,
          })
        );
      }
    } finally {
      setLoading(false);
    }
  }

  // ===================================================================
  // SEARCH
  // ===================================================================
  async function handleSearch() {
    const q = searchText.trim();
    if (q.length < 3) return;

    setSearching(true);
    setLoading(true);

    try {
      const url = new URL(`${API_BASE}/api/activities-search`);
      url.searchParams.append("query", q);

      if (activeTab === "mine") {
        // Coach private activities
        url.searchParams.append("visibility", "private");
        url.searchParams.append("createdBy", user.doc_id);  // identify coach
      } else {
        // Public activities
        url.searchParams.append("visibility", "public");
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        setSearchResults(data.results);
      }
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearching(false);
    setSearchText("");
    setSearchResults([]);
    setPage(1);
    loadPage(1);
  }

  // ===================================================================
  // PAGINATION
  // ===================================================================
  const total = totalMap[filterKey] || 0;
  const totalPages = Math.ceil(total / 8);

  function goNext() {
    if (searching) return;
    if (page < totalPages) {
      setPage(page + 1);
      loadPage(page + 1);
    }
  }

  function goPrev() {
    if (searching) return;
    if (page > 1) {
      setPage(page - 1);
      loadPage(page - 1);
    }
  }

  // ===================================================================
  // RENDER ITEM
  // ===================================================================
  function renderItem(act, idx) {
    return (
      <li
        key={act.id}
        onClick={() => activeTab === "mine" ? navigate(`/coach-activities/${act.id}`) : navigate(`/activities/${act.id}`, { state: { from: "coach-side" }})}
        className="p-2 flex flex-col gap-1 hover:bg-gray-50 cursor-pointer"
      >
        <div className="flex items-start gap-1 text-left">
          <span className="text-gray-500 shrink-0">
            {idx + 1 + (!searching ? (page - 1) * 8 : 0)}.
          </span>

          <span className="text-blue-600 underline hyphens-auto">
            {act.name}
          </span>
        </div>

        <div className="flex flex-wrap gap-1 justify-end">
          <StatusBadge status={act.status} />
          <TypeBadge type={act.type} />
          {act.type === "tournament" && (
            <RankingBadge ranking={act.rankingType} />
          )}
        </div>
      </li>
    );
  }

  // ===================================================================
  // UI
  // ===================================================================
  const isDualRole =
    user?.role?.includes("admin") && user?.role?.includes("coach");

  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />
      <PageHeader title="Activities" backTo="/dashboard" />

      {isDualRole && (
        <div className="w-full flex justify-end mb-2">
          <button
            onClick={() => navigate("/other-coaches-list")}
            className="
              underline 
              text-blue-600 
              hover:text-blue-800 
              transition 
              text-xs sm:text-sm
            "
          >
            View Other Coaches' Activities
          </button>
        </div>
      )}
      {/* ===================================================== */}
      {/* TABS */}
      {/* ===================================================== */}
      <div className="flex justify-center border-b mt-2 mb-4">
        <button
          className={`p-2 border text-sm ${
            activeTab === "mine"
              ? "bg-blue-600 text-white"
              : "bg-white-200 text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("mine");
            clearSearch();
          }}
        >
          My Activities
        </button>

        <button
          className={`p-2 border text-sm ${
            activeTab === "public"
              ? "bg-blue-600 text-white"
              : "bg-white-200 text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("public");
            clearSearch();
          }}
        >
          General Activities
        </button>
      </div>

      {/* ===================================================== */}
      {/* FILTER ROW */}
      {/* ===================================================== */}
      <div className={`flex flex-col w-full gap-2 ${searchText.length > 0 ? "opacity-40 pointer-events-none" : ""}`}>
        {/* Row 1 */}
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-wrap gap-3 flex-grow">
            {/* Status */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-2 py-1"
            >
              <option value="All">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="draft">Draft</option>
              <option value="full">Full</option>
              <option value="closed">Closed</option>
            </select>

            {/* Type */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border rounded px-2 py-1"
            >
              <option value="All">All Types</option>
              <option value="event">Event</option>
              <option value="tournament">Tournament</option>
              <option value="camp">Camp</option>
              <option value="seminar">Seminar</option>
              <option value="workshop">Workshop</option>
              <option value="talk">Talk</option>
              <option value="sparring">Sparring</option>
              <option value="fitness">Fitness</option>
              <option value="friendlyMatch">Friendly Match</option>
            </select>

            {/* Ranking type */}
            {typeFilter === "tournament" && (
              <select
                value={rankingTypeFilter}
                onChange={(e) => setRankingTypeFilter(e.target.value)}
                className="border rounded px-2 py-1"
              >
                <option value="All">All Rankings</option>
                <option value="AJSS">AJSS</option>
                <option value="National">National</option>
              </select>
            )}
          </div>

          {/* Add Activity (only for My Activities) */}
          {activeTab === "mine" && (
            <button
              onClick={() => navigate("/coach-activities/new")}
              className="p-3 bg-green-600 text-white rounded-full hover:bg-green-700"
            >
              <FaPlus />
            </button>
          )}
        </div>

        {/* Row 2 */}
        <div className="flex gap-3">
          {/* Reset */}
          <button
            onClick={() => {
              setStatusFilter("All");
              setTypeFilter("All");
              setRankingTypeFilter("All");
            }}
            className="p-2 text-gray-600 hover:text-black"
          >
            <FaFilterCircleXmark size={18} />
          </button>

          {/* Sort */}
          <button
            onClick={() => {
              setSortOrder(sortOrder === "latest" ? "oldest" : "latest");
              setPage(1);
              dispatch(resetCoachActivities());
            }}
            className="p-2 bg-gray-200 rounded"
          >
            {sortOrder === "latest" ? (
              <FaArrowDownShortWide size={18} />
            ) : (
              <FaArrowUpShortWide size={18} />
            )}
          </button>
        </div>
      </div>

      {/* ===================================================== */}
      {/* SEARCH */}
      {/* ===================================================== */}
      <div className="flex items-center gap-2 mt-2 mb-4">
        <div className="relative flex-grow">
          <input
            className="w-full border rounded-lg p-3 pr-10 placeholder:italic placeholder:text-gray-400"
            placeholder="Search activities (min 3 characters)..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {searchText.trim().length > 0 && (
            <button
              onClick={clearSearch}
              className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-500"
            >
              <FaTimes />
            </button>
          )}
        </div>

        <button
          onClick={handleSearch}
          disabled={searchText.trim().length < 3}
          className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <FaSearch />
        </button>
      </div>

      {/* ===================================================== */}
      {/* LIST */}
      {/* ===================================================== */}
      {searching ? (
        searchResults.length === 0 ? (
          <p className="text-gray-500">No matching activities found.</p>
        ) : (
          <ul className="divide-y rounded-lg bg-white shadow-sm">
            {searchResults.map((act, i) => renderItem(act, i))}
          </ul>
        )
      ) : activities.length === 0 ? (
        <p className="text-gray-500">No activities found.</p>
      ) : (
        <ul className="divide-y rounded-lg bg-white shadow-sm">
          {activities.map((act, i) => renderItem(act, i))}
        </ul>
      )}

      {/* ===================================================== */}
      {/* PAGINATION */}
      {/* ===================================================== */}
      {!searching && totalPages > 1 && (
        <div className="flex justify-center items-center gap-6 mt-4">
          <button
            disabled={page === 1}
            onClick={goPrev}
            className="disabled:opacity-30"
          >
            <FaChevronLeft size={18} />
          </button>

          <span className="text-gray-700">
            Page {page} / {totalPages}
          </span>

          <button
            disabled={page === totalPages}
            onClick={goNext}
            className="disabled:opacity-30"
          >
            <FaChevronRight size={18} />
          </button>
        </div>
      )}
    </MainContentLayout>
  );
}
